import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, BookOpen, Users, Library, GitBranch, BadgeCheck, Presentation, UserCircle, Send, GraduationCap, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useEntitlement } from '@/hooks/useEntitlement';
import BrandMark from './BrandMark';

const baseLinks = [
  { name: 'Encyclopedia', path: '/encyclopedia', icon: BookOpen },
  { name: 'Explore', path: '/gallery', icon: Users },
  { name: 'Hadith', path: '/hadith-grading', icon: BadgeCheck },
  { name: 'Binder', path: '/binder', icon: Library },
  { name: 'Fiqh Map', path: '/fiqh-map', icon: GitBranch },
];
const teachLink = { name: 'Teach', path: '/teacher', icon: Presentation };
const baseMoreLinks = [
  { name: 'Submit Scholar', path: '/submit', icon: Send },
  { name: 'Pricing', path: '/pricing', icon: GraduationCap },
];

const linkClass = ({ isActive }) =>
  `px-2 py-2 rounded-md text-sm font-medium transition duration-300 flex items-center whitespace-nowrap ${
    isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-600 hover:text-emerald-600'
  }`;

const AppNav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { configured, user } = useAuth();
  // Teacher Pro users get Teach up top; everyone else finds it under More.
  const { isPro } = useEntitlement();
  const links = isPro ? [...baseLinks, teachLink] : baseLinks;
  const moreLinks = isPro ? baseMoreLinks : [teachLink, ...baseMoreLinks];
  const accountPath = user ? '/account' : '/login';
  const accountLabel = user ? 'Account' : 'Sign in';

  return (
    <nav className="sticky top-0 z-50 border-b border-gold-300/70 bg-parchment/95 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 navbar-safe">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <BrandMark />
          </div>

          <div className="hidden lg:flex items-center space-x-1 min-w-0">
            {links.map((link) => {
              const IconComponent = link.icon;
              return (
                <NavLink key={link.path} to={link.path} className={linkClass}>
                  <IconComponent className="h-4 w-4 mr-1" />
                  {link.name}
                </NavLink>
              );
            })}
            <details className="group relative">
              <summary className="flex min-h-11 cursor-pointer list-none items-center rounded-md px-2 text-sm font-medium text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
                More <ChevronDown className="ml-1 h-4 w-4 transition group-open:rotate-180" />
              </summary>
              <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-emerald-100 bg-white p-1 shadow-lg">
                {moreLinks.map((link) => {
                  const Icon = link.icon;
                  return <NavLink key={link.path} to={link.path} className={linkClass}><Icon className="mr-2 h-4 w-4" />{link.name}</NavLink>;
                })}
              </div>
            </details>
            {configured && (
              <Link
                to={accountPath}
                title={accountLabel}
                aria-label={accountLabel}
                className="text-slate-600 hover:text-emerald-600 p-2 rounded-md transition duration-300"
              >
                <UserCircle className="h-5 w-5" />
              </Link>
            )}
          </div>

          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex min-h-11 min-w-11 items-center justify-center p-2 rounded-md text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className="lg:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-emerald-100">
            {[...links, ...moreLinks].map((link) => {
              const IconComponent = link.icon;
              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-md text-base font-medium flex items-center ${
                      isActive ? 'text-emerald-700 bg-emerald-50' : 'text-slate-600 hover:text-emerald-600 hover:bg-emerald-50'
                    }`
                  }
                  onClick={() => setIsMenuOpen(false)}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  {link.name}
                </NavLink>
              );
            })}
            {configured && (
              <Link
                to={accountPath}
                className="text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 block px-3 py-2 rounded-md text-base font-medium flex items-center"
                onClick={() => setIsMenuOpen(false)}
              >
                <UserCircle className="h-4 w-4 mr-2" />
                {accountLabel}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default AppNav;
